<template>
    <el-dialog
        :visible="visible"
        :title="dialogTitle"
        width="660px"
        custom-class="rcv-dialog rcv-probation-eval-dialog"
        :close-on-click-modal="false"
        append-to-body
        @update:visible="close"
        @closed="onClosed"
    >
        <div v-if="candidate" class="rcv-eval-body">
            <!-- Cảnh báo phân quyền nếu là HR -->
            <el-alert
                v-if="isHrDepartment"
                title="Quyền hạn bị giới hạn"
                type="error"
                description="Chỉ Teamlead mới có quyền điền kết quả đánh giá thử việc cho ứng viên."
                show-icon
                :closable="false"
                class="mb-3"
            />

            <!-- Thông tin ứng viên tóm tắt -->
            <div class="rcv-eval-cand">
                <div class="rcv-eval-cand__avatar">
                    <i class="el-icon-medal"></i>
                </div>
                <div class="rcv-eval-cand__info">
                    <div class="rcv-eval-cand__head">
                        <span class="rcv-eval-cand__name">{{ candidate.name || "Chưa có tên" }}</span>
                        <span class="rcv-eval-cand__id">#{{ candidate.id }}</span>
                        <span class="rcv-eval-cand__badge">
                            <i class="el-icon-user"></i> Đánh giá thử việc (Teamlead)
                        </span>
                    </div>
                    <div class="rcv-eval-cand__meta">
                        <span v-if="candidate.phone"><i class="el-icon-phone-outline"></i> {{ candidate.phone }}</span>
                        <span v-if="candidate.email"><i class="el-icon-message"></i> {{ candidate.email }}</span>
                        <span v-if="jobTitle(candidate)"><i class="el-icon-suitcase"></i> {{ jobTitle(candidate) }}</span>
                    </div>
                </div>
            </div>

            <!-- Form Đánh giá Thử việc -->
            <el-form
                ref="probationFormRef"
                :model="form"
                :rules="rules"
                :disabled="isHrDepartment"
                label-position="top"
                size="small"
                class="rcv-eval-form"
            >
                <!-- Lựa chọn Kết quả Thử việc: Pass / Fail -->
                <el-form-item label="Kết quả đánh giá thử việc" prop="result" required>
                    <div class="rcv-result-cards">
                        <div
                            class="rcv-result-card rcv-result-card--pass"
                            :class="{ 'is-active': form.result === 'pass' }"
                            @click="setResult('pass')"
                        >
                            <div class="rcv-result-card__icon">
                                <i class="el-icon-circle-check"></i>
                            </div>
                            <div class="rcv-result-card__content">
                                <div class="rcv-result-card__title">ĐẠT (Pass)</div>
                                <div class="rcv-result-card__desc">
                                    Đạt thử việc, điền Mức lương chính thức 
                                </div>
                            </div>
                            <div class="rcv-result-card__radio">
                                <i :class="form.result === 'pass' ? 'el-icon-success' : 'el-icon-circle-check'"></i>
                            </div>
                        </div>

                        <div
                            class="rcv-result-card rcv-result-card--fail"
                            :class="{ 'is-active': form.result === 'fail' }"
                            @click="setResult('fail')"
                        >
                            <div class="rcv-result-card__icon">
                                <i class="el-icon-circle-close"></i>
                            </div>
                            <div class="rcv-result-card__content">
                                <div class="rcv-result-card__title">KHÔNG ĐẠT (Fail)</div>
                                <div class="rcv-result-card__desc">
                                    Không đạt hoặc out thử việc 
                                </div>
                            </div>
                            <div class="rcv-result-card__radio">
                                <i :class="form.result === 'fail' ? 'el-icon-error' : 'el-icon-circle-close'"></i>
                            </div>
                        </div>
                    </div>
                </el-form-item>

                <!-- ================== TRƯỜNG HỢP 1: PASS ================== -->
                <transition name="el-fade-in-linear">
                    <div v-if="form.result === 'pass'" class="rcv-pass-section">
                        <div class="rcv-section-head text-success mb-3">
                            <i class="el-icon-circle-check font-bold mr-1"></i>
                            <span class="font-bold">Thông tin nhân sự Đạt thử việc</span>
                        </div>

                        <!-- Mức lương chính thức (official_salary) -->
                        <el-form-item label="Mức lương chính thức (VNĐ)" prop="official_salary" required>
                            <el-input
                                ref="salaryInput"
                                v-model="displaySalary"
                                placeholder="VD: 15.000.000"
                                class="w-100"
                                clearable
                                @input="handleSalaryInput"
                                @clear="handleSalaryClear"
                            >
                                <template slot="append">VNĐ</template>
                            </el-input>
                            <div v-if="form.official_salary" class="rcv-salary-text">
                                <b>{{ formatMoney(form.official_salary) }} VNĐ</b>
                            </div>
                        </el-form-item>
                    </div>
                </transition>

                <!-- ================== TRƯỜNG HỢP 2: FAIL ================== -->
                <transition name="el-fade-in-linear">
                    <div v-if="form.result === 'fail'" class="rcv-fail-section">
                        <div class="rcv-section-head text-danger mb-3">
                            <i class="el-icon-circle-close font-bold mr-1"></i>
                            <span class="font-bold">Thông tin nhân sự Không đạt thử việc</span>
                        </div>

                        <!-- Ngày out thử việc (probation_exit_date) -->
                        <el-form-item label="Ngày out thử việc" prop="probation_exit_date" required>
                            <el-date-picker
                                v-model="form.probation_exit_date"
                                type="date"
                                value-format="yyyy-MM-dd"
                                format="dd/MM/yyyy"
                                placeholder="Chọn ngày out"
                                class="w-100"
                            />
                        </el-form-item>

                        <!-- Lý do out thử việc (probation_exit_reason) -->
                        <el-form-item label="Lý do out thử việc" prop="probation_exit_reason" required>
                            <el-input
                                v-model="form.probation_exit_reason"
                                type="textarea"
                                :rows="3"
                                placeholder="Ghi rõ lý do ứng viên không đạt / out thử việc (Tìm được việc khác, lý do cá nhân, không phù hợp môi trường, chưa đạt KPI...)..."
                                maxlength="500"
                                show-word-limit
                            />
                        </el-form-item>

                        <!-- Gợi ý nhanh lý do out -->
                        <div class="rcv-quick-reasons mb-3">
                            <span class="rcv-quick-reasons__label">Gợi ý nhanh lý do:</span>
                            <div class="rcv-quick-reasons__tags">
                                <el-tag
                                    v-for="(r, idx) in quickExitReasons"
                                    :key="idx"
                                    size="mini"
                                    class="rcv-quick-tag"
                                    @click="selectQuickExitReason(r)"
                                >
                                    + {{ r }}
                                </el-tag>
                            </div>
                        </div>

                        <!-- Checkbox gửi Mail Cảm ơn & Dừng hành trình -->
                        <div class="rcv-mail-box mb-3">
                            <div class="rcv-mail-box__header">
                                <el-checkbox v-model="form.send_thank_you_mail">
                                    <span class="font-bold">Tự động gửi Email thông báo & Cảm ơn</span>
                                </el-checkbox>
                            </div>
                            <div class="rcv-mail-box__desc">
                                Hệ thống sẽ tự động cập nhật trạng thái sang <b>Đã nhập kết quả đánh giá thử việc - Fail</b> và gửi email cảm ơn nếu được chọn.
                            </div>
                        </div>
                    </div>
                </transition>
            </el-form>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button size="small" :disabled="submitting" @click="close">Huỷ</el-button>
            <el-button
                size="small"
                :type="form.result === 'fail' ? 'danger' : 'success'"
                :loading="submitting"
                :disabled="isHrDepartment"
                icon="el-icon-check"
                @click="handleSubmit"
            >
                {{ form.result === 'fail' ? 'Xác nhận Không đạt thử việc' : 'Xác nhận Đạt & Lưu mức lương chính thức' }}
            </el-button>
        </span>
    </el-dialog>
</template>

<script>
import moment from "moment-timezone";
import { mapGetters } from "vuex";
import {
    CAN_DANH_GIA_THU_VIEC,
    DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS,
    DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL,
    DA_NHAN_VIEC,
} from "@/core/config/recruitmentCvs/recruitmentCvsOption";

export default {
    name: "ProbationEvaluationDialog",
    props: {
        visible: { type: Boolean, default: false },
        candidate: { type: Object, default: null },
        orders: { type: Array, default: () => [] },
        isHrDepartment: { type: Boolean, default: false },
        submitting: { type: Boolean, default: false },
    },
    data() {
        return {
            CAN_DANH_GIA_THU_VIEC,
            DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS,
            DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL,
            displaySalary: "",
            form: {
                result: "pass", // 'pass' | 'fail'
                official_salary: null,
                probation_exit_date: "",
                probation_exit_reason: "",
                send_thank_you_mail: true,
                score: 8,
                note: "",
            },
            quickExitReasons: [
                "Ứng viên tìm được công việc khác phù hợp hơn",
                "Ứng viên chủ động xin nghỉ do lý do cá nhân / gia đình",
                "Không đáp ứng yêu cầu chuyên môn & KPI thử việc",
                "Không phù hợp văn hoá / môi trường làm việc",
                "Khoảng cách đi lại quá xa, không thuận tiện",
                "Ý thức kỷ luật / Giờ giấc làm việc chưa tốt",
            ],
            rules: {
                result: [{ required: true, message: "Vui lòng chọn kết quả thử việc", trigger: "change" }],
                official_salary: [
                    {
                        validator: (rule, value, callback) => {
                            if (this.form.result === "pass") {
                                const num = Number(this.form.official_salary);
                                if (!num || num <= 0) {
                                    callback(new Error("Vui lòng nhập mức lương chính thức"));
                                    return;
                                }
                            }
                            callback();
                        },
                        trigger: ["blur", "change"],
                    },
                ],
                probation_exit_date: [
                    {
                        validator: (rule, value, callback) => {
                            if (this.form.result === "fail" && !this.form.probation_exit_date) {
                                callback(new Error("Vui lòng chọn ngày out thử việc"));
                                return;
                            }
                            callback();
                        },
                        trigger: ["blur", "change"],
                    },
                ],
                probation_exit_reason: [
                    {
                        validator: (rule, value, callback) => {
                            if (this.form.result === "fail" && (!value || !value.trim())) {
                                callback(new Error("Vui lòng nhập lý do out thử việc"));
                                return;
                            }
                            callback();
                        },
                        trigger: ["blur", "change"],
                    },
                ],
            },
        };
    },
    computed: {
        ...mapGetters(["currentUser"]),
        dialogTitle() {
            if (!this.candidate) return "Đánh giá thử việc (Teamlead)";
            return `Điền kết quả thử việc: ${this.candidate.name || `#${this.candidate.id}`} (Teamlead)`;
        },
        currentUserName() {
            const u = this.currentUser || {};
            return u.name || u.full_name || u.username || "Teamlead";
        },
    },
    watch: {
        visible(val) {
            if (val) {
                this.initFormData();
            }
        },
        candidate: {
            deep: true,
            handler() {
                if (this.visible) this.initFormData();
            },
        },
    },
    methods: {
        initFormData() {
            if (!this.candidate) return;
            const cand = this.candidate;
            const isFailStatus = Number(cand.status) === DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL || cand.probation_result === "failed" || cand.probation_result === "fail";
            const isPassStatus = Number(cand.status) === DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS || cand.probation_result === "pass";

            const result = isFailStatus ? "fail" : "pass";
            const salary = cand.official_salary !== undefined && cand.official_salary !== null
                ? cand.official_salary
                : null;

            const exitReason = cand.probation_exit_reason || cand.probation_reason || (isFailStatus ? (cand.probation_note || "") : "");
            const exitDate = cand.probation_exit_date || moment().format("YYYY-MM-DD");

            this.form = {
                result,
                official_salary: salary,
                probation_exit_date: exitDate,
                probation_exit_reason: exitReason,
                send_thank_you_mail: true,
                score: cand.probation_score || (isPassStatus ? 8 : 4),
                note: isPassStatus ? (cand.probation_note || "") : "",
            };

            this.displaySalary = salary ? Number(salary).toLocaleString("vi-VN") : "";

            this.$nextTick(() => {
                if (this.$refs.probationFormRef) {
                    this.$refs.probationFormRef.clearValidate();
                }
            });
        },
        handleSalaryClear() {
            this.displaySalary = "";
            this.form.official_salary = null;
            if (this.$refs.probationFormRef) {
                this.$refs.probationFormRef.validateField("official_salary");
            }
        },
        handleSalaryInput(val) {
            if (!val) {
                this.displaySalary = "";
                this.form.official_salary = null;
                if (this.$refs.probationFormRef) {
                    this.$refs.probationFormRef.validateField("official_salary");
                }
                return;
            }

            const inputEl = this.$refs.salaryInput && this.$refs.salaryInput.$refs.input;
            const oldVal = String(val);
            const oldCursorPos = inputEl ? inputEl.selectionStart : null;
            const oldDigitsBeforeCursor = oldCursorPos !== null
                ? oldVal.slice(0, oldCursorPos).replace(/\D/g, "").length
                : 0;

            const digits = oldVal.replace(/\D/g, "");
            if (!digits) {
                this.displaySalary = "";
                this.form.official_salary = null;
                if (this.$refs.probationFormRef) {
                    this.$refs.probationFormRef.validateField("official_salary");
                }
                return;
            }

            const num = Number(digits);
            this.form.official_salary = num;
            const formatted = num.toLocaleString("vi-VN");
            this.displaySalary = formatted;

            this.$nextTick(() => {
                if (inputEl) {
                    inputEl.value = formatted;
                    if (oldCursorPos !== null) {
                        let newCursor = 0;
                        let digitCount = 0;
                        for (let i = 0; i < formatted.length; i++) {
                            if (/\d/.test(formatted[i])) {
                                digitCount++;
                            }
                            if (digitCount === oldDigitsBeforeCursor) {
                                newCursor = i + 1;
                                break;
                            }
                        }
                        if (newCursor === 0 && oldDigitsBeforeCursor === 0) {
                            newCursor = 0;
                        } else if (digitCount < oldDigitsBeforeCursor) {
                            newCursor = formatted.length;
                        }
                        inputEl.setSelectionRange(newCursor, newCursor);
                    }
                }
                if (this.$refs.probationFormRef) {
                    this.$refs.probationFormRef.validateField("official_salary");
                }
            });
        },
        setResult(r) {
            this.form.result = r;
            if (r === "fail" && !this.form.probation_exit_date) {
                this.form.probation_exit_date = moment().format("YYYY-MM-DD");
            }
            this.$nextTick(() => {
                if (this.$refs.probationFormRef) {
                    this.$refs.probationFormRef.clearValidate();
                }
            });
        },
        selectQuickExitReason(reason) {
            if (!this.form.probation_exit_reason) {
                this.form.probation_exit_reason = reason;
            } else if (!this.form.probation_exit_reason.includes(reason)) {
                this.form.probation_exit_reason = `${this.form.probation_exit_reason}; ${reason}`;
            }
            if (this.$refs.probationFormRef) {
                this.$refs.probationFormRef.validateField("probation_exit_reason");
            }
        },
        jobTitle(cand) {
            if (!cand) return "";
            const found = this.orders.find((o) =>
                String(o.id) === String(cand.job_id) ||
                String(o.id) === String(cand.order_id) ||
                String(o.id) === String(cand.raw_job_id) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(cand.job_id)) ||
                (o.recruitment_job_id && String(o.recruitment_job_id) === String(cand.raw_job_id))
            );
            return found ? (found.title || found.position_title || "") : (cand.order_title || "");
        },
        formatMoney(num) {
            if (num === null || num === undefined || num === "") return "0";
            return Number(num).toLocaleString("vi-VN");
        },
        handleSubmit() {
            if (this.isHrDepartment) {
                this.$message.warning("Chỉ Teamlead mới có quyền điền kết quả thử việc");
                return;
            }

            this.$refs.probationFormRef.validate((valid) => {
                if (!valid) return;
                const isPass = this.form.result === "pass";

                const targetStatus = isPass
                    ? DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_PASS
                    : DA_NHAP_KET_QUA_DANH_GIA_THU_VIEC_FAIL;

                let noteContent = isPass
                    ? `[Đánh giá thử việc - ĐẠT (Pass)]`
                    : `[Đánh giá thử việc - KHÔNG ĐẠT]`;

                if (isPass && this.form.official_salary) {
                    noteContent += `\nMức lương chính thức: ${this.formatMoney(this.form.official_salary)} VNĐ`;
                }
                if (!isPass) {
                    if (this.form.probation_exit_date) {
                        noteContent += `\nNgày out: ${this.form.probation_exit_date}`;
                    }
                    if (this.form.probation_exit_reason) {
                        noteContent += `\nLý do out: ${this.form.probation_exit_reason}`;
                    }
                }

                const finalReason = isPass ? null : (this.form.probation_exit_reason || null);
                const finalExitDate = isPass ? null : (this.form.probation_exit_date || null);

                const existingSalary = (this.candidate && this.candidate.salary) || null;
                const updatedCandidate = {
                    ...this.candidate,
                    status: targetStatus,
                    probation_result: isPass ? "pass" : "failed",
                    salary: existingSalary,
                    official_salary: isPass ? this.form.official_salary : null,
                    probation_date: isPass ? ((this.candidate && this.candidate.probation_date) || null) : null,
                    probation_reason: finalReason,
                    probation_exit_date: finalExitDate,
                    probation_exit_reason: finalReason,
                    probation_note: finalReason || "",
                };

                this.$emit("submit", {
                    id: this.candidate.id,
                    status: targetStatus,
                    result: isPass ? "pass" : "failed",
                    salary: existingSalary,
                    official_salary: isPass ? this.form.official_salary : null,
                    probation_date: isPass ? ((this.candidate && this.candidate.probation_date) || null) : null,
                    probation_reason: finalReason,
                    probation_exit_date: finalExitDate,
                    probation_exit_reason: finalReason,
                    send_thank_you_mail: !isPass && this.form.send_thank_you_mail,
                    score: this.form.score,
                    note: noteContent,
                    candidate: updatedCandidate,
                });
            });
        },
        close() {
            this.$emit("update:visible", false);
        },
        onClosed() {
            this.initFormData();
        },
    },
};
</script>

<style scoped>
.rcv-eval-cand {
    display: flex;
    align-items: center;
    gap: 14px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 12px 16px;
    margin-bottom: 18px;
}
.rcv-eval-cand__avatar {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: #fef3c7;
    color: #d97706;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
}
.rcv-eval-cand__info {
    flex: 1;
    min-width: 0;
}
.rcv-eval-cand__head {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 4px;
}
.rcv-eval-cand__name {
    font-weight: 700;
    font-size: 15px;
    color: #0f172a;
}
.rcv-eval-cand__id {
    font-size: 12px;
    color: #94a3b8;
}
.rcv-eval-cand__badge {
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.rcv-eval-cand__meta {
    display: flex;
    gap: 12px;
    font-size: 12px;
    color: #64748b;
    flex-wrap: wrap;
}
.rcv-eval-cand__meta i {
    margin-right: 2px;
}

/* Result Cards */
.rcv-result-cards {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}
.rcv-result-card {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 10px;
    border: 2px solid #e2e8f0;
    background: #ffffff;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
}
.rcv-result-card:hover {
    border-color: #cbd5e1;
    transform: translateY(-1px);
}
.rcv-result-card__icon {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
}
.rcv-result-card--pass .rcv-result-card__icon {
    background: #d1fae5;
    color: #059669;
}
.rcv-result-card--pass.is-active {
    border-color: #059669;
    background: #f0fdf4;
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.15);
}
.rcv-result-card--pass.is-active .rcv-result-card__radio {
    color: #059669;
}

.rcv-result-card--fail .rcv-result-card__icon {
    background: #fee2e2;
    color: #dc2626;
}
.rcv-result-card--fail.is-active {
    border-color: #dc2626;
    background: #fef2f2;
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.15);
}
.rcv-result-card--fail.is-active .rcv-result-card__radio {
    color: #dc2626;
}

.rcv-result-card__content {
    flex: 1;
    min-width: 0;
}
.rcv-result-card__title {
    font-weight: 700;
    font-size: 14px;
    color: #0f172a;
    margin-bottom: 2px;
}
.rcv-result-card__desc {
    font-size: 11px;
    color: #64748b;
    line-height: 1.3;
}
.rcv-result-card__radio {
    font-size: 18px;
    color: #cbd5e1;
}

/* Pass Section */
.rcv-pass-section {
    margin-top: 14px;
    padding: 16px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 10px;
}
.rcv-section-head {
    font-size: 13.5px;
    display: flex;
    align-items: center;
}
.rcv-salary-text {
    font-size: 12px;
    color: #059669;
    margin-top: 4px;
}

/* Fail Section */
.rcv-fail-section {
    margin-top: 14px;
    padding: 16px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 10px;
}

.rcv-quick-reasons {
    margin-top: 8px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
    flex-wrap: wrap;
}
.rcv-quick-reasons__label {
    font-size: 12px;
    color: #64748b;
    font-weight: 600;
}
.rcv-quick-reasons__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
}
.rcv-quick-tag {
    cursor: pointer;
    background: #ffffff;
    border-color: #fca5a5;
    color: #b91c1c;
    transition: all 0.15s;
}
.rcv-quick-tag:hover {
    background: #fee2e2;
}

.rcv-mail-box {
    padding: 12px;
    background: #ffffff;
    border: 1px dashed #fca5a5;
    border-radius: 8px;
}
.rcv-mail-box__desc {
    font-size: 11px;
    color: #64748b;
    margin-top: 4px;
    margin-left: 24px;
}

@media (max-width: 600px) {
    .rcv-result-cards {
        grid-template-columns: 1fr;
    }
}
</style>
